/**
 * The week-by-week strip from the solver's bin-packing (PLAN §3, step 7).
 *
 * Capacity is a single pool of minutes, but the schedule is whole weekly
 * buckets, and an entry longer than what's left of a week continues into the
 * next one. The solver already models that with `continued`/`continues`; this
 * draws the corresponding end of the block open so a 306-minute season reads
 * as one thing spanning two weeks rather than two unrelated blocks.
 *
 * Bar heights are proportional to the week's capacity, not to the busiest
 * week — a light week has to *look* light, which it doesn't if every week is
 * normalised to full height.
 */

import { formatMinutes } from "@/components/entry/RuntimeChip.tsx";
import type { LegPlan, PlannedEntry } from "@/lib/solver/index.ts";
import styles from "./plan.module.css";

const WEEKDAY = { month: "short", day: "numeric" } as const;

function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, WEEKDAY);
}

export function WeekPlanTimeline({ leg }: { leg: LegPlan }) {
  if (leg.weeks.length === 0) return null;

  const byId = new Map<string, PlannedEntry>(leg.selected.map((e) => [e.entryId, e]));
  // Scale against a full week's budget, so a light week reads as light. A week
  // can still exceed it — the packer never splits an entry, so one long season
  // can overflow its bucket — hence `busiest` as the actual ceiling.
  const weeklyCapacity = Math.max(1, Math.round(leg.hoursPerWeek * 60));
  const busiest = Math.max(weeklyCapacity, ...leg.weeks.map((w) => w.minutes));

  return (
    <div className={styles.timeline}>
      <div className={styles.timelineHead}>
        <h4 className={styles.timelineTitle}>Week by week</h4>
        <p className={styles.timelineNote}>
          {leg.weeks.length} {leg.weeks.length === 1 ? "week" : "weeks"} at {leg.hoursPerWeek}{" "}
          h/week
          {leg.projectedFinish ? ` · finishes ${shortDate(leg.projectedFinish)}` : ""}
        </p>
      </div>

      <ol className={styles.weeks}>
        {leg.weeks.map((week) => {
          const over = week.minutes > weeklyCapacity;
          return (
            <li key={week.index} className={styles.week}>
              <div className={styles.weekBar} aria-hidden="true">
                {week.items.map((item) => {
                  const planned = byId.get(item.entryId);
                  const pct = (item.minutes / busiest) * 100;
                  return (
                    <span
                      key={`${item.entryId}-${week.index}`}
                      className={[
                        styles.block,
                        item.continued ? styles.blockContinued : "",
                        item.continues ? styles.blockContinues : "",
                        planned?.mandatory ? styles.blockMandatory : "",
                        planned?.compressed ? styles.blockCompressed : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{ height: `${Math.max(pct, 2)}%` }}
                      title={`${planned?.title ?? item.entryId}: ${formatMinutes(item.minutes)}${
                        item.continues ? " (continues)" : ""
                      }${item.continued ? " (continued)" : ""}`}
                    />
                  );
                })}
              </div>

              <div className={styles.weekMeta}>
                <span className={styles.weekIndex}>W{week.index}</span>
                <span className={`${styles.weekMinutes} ${over ? styles.weekOver : ""}`}>
                  {formatMinutes(week.minutes)}
                </span>
              </div>

              <span className={styles.srOnly}>
                Week {week.index}, {shortDate(week.startDate)} to {shortDate(week.endDate)}:{" "}
                {formatMinutes(week.minutes)} across {week.items.length}{" "}
                {week.items.length === 1 ? "stop" : "stops"}.
                {week.items
                  .map((i) => byId.get(i.entryId)?.title)
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
